import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '@/api/client';
import { adminApi, teamApi } from '@/api/endpoints';
import type { AdminMatchday, Match, Player, Question, Team, User } from '@/api/types';
import { Alert, Button, Card, Notice, Spinner } from '@/components/ui';
import {
  NONE_VALUE,
  playerValueToId,
  type PlayerDraft,
} from '@/features/ergebnistipp/categoryDraft';
import { MatchRow, type PredictionDraft } from '@/features/ergebnistipp/MatchRow';
import { attendanceIntervals } from '@/lib/attendance';
import { formatDateTime, isPastDeadline } from '@/lib/datetime';
import { useAsync } from '@/lib/useAsync';
import styles from './admin.module.css';

const TARGET_TEAM_HINT = 'münster';

interface Selectors {
  users: User[];
  matchdays: AdminMatchday[];
}

export function AdminPredictionsPage() {
  const { data, loading, error } = useAsync<Selectors>(async (signal) => {
    const [users, matchdays] = await Promise.all([
      adminApi.listUsers('active', signal),
      adminApi.listMatchdays(signal),
    ]);
    return { users, matchdays };
  }, []);

  const [userId, setUserId] = useState<number | ''>('');
  const [matchdayId, setMatchdayId] = useState<number | ''>('');

  if (loading) return <Spinner label="Daten werden geladen…" />;
  if (error) return <Alert>{error}</Alert>;

  const users = data?.users ?? [];
  const matchdays = [...(data?.matchdays ?? [])].sort((a, b) => a.sort_order - b.sort_order);
  const matchday = matchdays.find((m) => m.id === matchdayId);

  return (
    <>
      <Card>
        <h2>Tipps nacherfassen</h2>
        <p className="muted">
          Mitglied und Spieltag wählen, dann die Tipps im Namen des Mitglieds eintragen. Nach
          Tippschluss gesperrt.
        </p>
        <div className={styles.inlineForm}>
          <label className={styles.field} style={{ marginBottom: 0, flex: '1 1 180px' }}>
            <span className={styles.label}>Mitglied</span>
            <select
              className={styles.input}
              value={userId === '' ? '' : String(userId)}
              onChange={(e) => setUserId(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">— wählen —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.display_name} ({u.email})
                </option>
              ))}
            </select>
          </label>
          <label className={styles.field} style={{ marginBottom: 0, flex: '1 1 180px' }}>
            <span className={styles.label}>Spieltag</span>
            <select
              className={styles.input}
              value={matchdayId === '' ? '' : String(matchdayId)}
              onChange={(e) => setMatchdayId(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">— wählen —</option>
              {matchdays.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Card>

      {users.length === 0 && (
        <Card>
          <p className="muted">Keine aktiven Mitglieder vorhanden.</p>
        </Card>
      )}

      {/* Bonus-Spieltage haben per Contract §14 keine `matches` — dort nur die
          Fragen. Normale Spieltage können zusätzlich Fragen haben (§14.5). */}
      {userId !== '' && matchday && matchday.kind !== 'bonus' && (
        <PredictionEditor key={`${userId}-${matchday.id}`} userId={userId} matchday={matchday} />
      )}
      {userId !== '' && matchday && (
        <QuestionAnswerEditor
          key={`q-${userId}-${matchday.id}`}
          userId={userId}
          matchday={matchday}
        />
      )}
    </>
  );
}

/** Slots je Frage: ein Wert bei team/multiple_choice, `slot_count` bei team_ranking. */
function slotsFor(q: Question): number {
  return q.answer_type === 'team_ranking' ? (q.slot_count ?? 0) : 1;
}

/**
 * Nacherfassung der Fragen-Antworten (§14.5) — für Bonus-Spieltage die einzige
 * Eingabe, an normalen Spieltagen zusätzlich zu den Spiel-Tipps. Wie bei den
 * Kategorie-Tipps gilt §13.0: fremde Antworten werden NICHT vorbefüllt, Eingaben
 * überschreiben blind.
 */
function QuestionAnswerEditor({ userId, matchday }: { userId: number; matchday: AdminMatchday }) {
  const locked = isPastDeadline(matchday.deadline_at);
  const isBonus = matchday.kind === 'bonus';

  const { data, loading, error } = useAsync<{ questions: Question[]; teams: Team[] }>(
    async (signal) => {
      const [questions, teams] = await Promise.all([
        adminApi.listQuestions(matchday.id, signal),
        teamApi.listLeague(signal).catch(() => [] as Team[]),
      ]);
      return {
        questions: [...questions].sort((a, b) => a.sort_order - b.sort_order),
        teams,
      };
    },
    [matchday.id],
  );

  const [drafts, setDrafts] = useState<Record<number, string[]>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedCount, setSavedCount] = useState<number | null>(null);

  const questions = useMemo(() => data?.questions ?? [], [data]);
  const teams = useMemo(() => data?.teams ?? [], [data]);

  useEffect(() => {
    setDrafts({});
    setSavedCount(null);
  }, [userId, matchday.id]);

  function handleChange(questionId: number, slotIndex: number, value: string) {
    setSavedCount(null);
    setDrafts((prev) => {
      const current = [...(prev[questionId] ?? [])];
      current[slotIndex] = value;
      return { ...prev, [questionId]: current };
    });
  }

  async function save() {
    setSaving(true);
    setSaveError(null);
    setSavedCount(null);
    try {
      let saved = 0;
      for (const q of questions) {
        const v = drafts[q.id] ?? [];
        if (q.answer_type === 'team_ranking') {
          const slots = slotsFor(q);
          const filled = Array.from({ length: slots }, (_, i) => v[i] ?? '');
          if (filled.some((s) => s === '')) continue;
          await adminApi.putQuestionAnswer(q.id, {
            user_id: userId,
            team_ids: filled.map((s) => Number(s)),
          });
        } else if (q.answer_type === 'multiple_choice') {
          if (!v[0]) continue;
          await adminApi.putQuestionAnswer(q.id, { user_id: userId, option_id: Number(v[0]) });
        } else {
          if (!v[0]) continue;
          await adminApi.putQuestionAnswer(q.id, { user_id: userId, team_id: Number(v[0]) });
        }
        saved += 1;
      }
      setSavedCount(saved);
    } catch (err) {
      if (err instanceof ApiError && err.status === 423) {
        setSaveError('Tippschluss überschritten — Antworten sind gesperrt.');
      } else {
        setSaveError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
      }
    } finally {
      setSaving(false);
    }
  }

  if (loading) return isBonus ? <Spinner label="Fragen werden geladen…" /> : null;
  if (error) return <Alert>{error}</Alert>;
  // An normalen Spieltagen ohne Fragen bleibt der Block komplett unsichtbar.
  if (questions.length === 0) {
    return isBonus ? (
      <Alert>Für diesen Bonus-Spieltag sind noch keine Fragen hinterlegt.</Alert>
    ) : null;
  }

  return (
    <>
      {isBonus && <p className="muted">Tippschluss: {formatDateTime(matchday.deadline_at)}</p>}

      {isBonus && locked && (
        <Notice>
          Der Tippschluss ist überschritten. Antworten können nicht mehr erfasst werden.
        </Notice>
      )}

      {questions.map((q) => {
        const draft = drafts[q.id] ?? [];
        return (
          <Card key={q.id}>
            <div className={styles.rowTitle}>{q.prompt}</div>
            <div className="muted" style={{ marginBottom: 8 }}>
              {q.points} Punkte
              {q.answer_type === 'team_ranking' ? ' / richtig platziertes Team' : ''}
            </div>

            {q.answer_type === 'team_ranking' ? (
              Array.from({ length: slotsFor(q) }, (_, i) => (
                <label key={i} className={styles.field}>
                  <span className={styles.label}>Platz {i + 1}</span>
                  <select
                    className={styles.categorySelect}
                    disabled={locked}
                    value={draft[i] ?? ''}
                    onChange={(e) => handleChange(q.id, i, e.target.value)}
                  >
                    <option value="">— Mannschaft wählen —</option>
                    {teams.map((t) => (
                      <option key={t.id} value={String(t.id)}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </label>
              ))
            ) : (
              <label className={styles.field}>
                <span className={styles.label}>Antwort</span>
                <select
                  className={styles.categorySelect}
                  disabled={locked}
                  value={draft[0] ?? ''}
                  onChange={(e) => handleChange(q.id, 0, e.target.value)}
                >
                  <option value="">— wählen —</option>
                  {q.answer_type === 'multiple_choice'
                    ? (q.options ?? []).map((o) => (
                        <option key={o.id} value={String(o.id)}>
                          {o.label}
                        </option>
                      ))
                    : teams.map((t) => (
                        <option key={t.id} value={String(t.id)}>
                          {t.name}
                        </option>
                      ))}
                </select>
              </label>
            )}
          </Card>
        );
      })}

      {!locked && (
        <div className={styles.saveBar}>
          {saveError && <Alert>{saveError}</Alert>}
          <Button onClick={() => void save()} disabled={saving}>
            {saving ? 'Speichern…' : 'Antworten speichern'}
          </Button>
          {savedCount !== null && (
            <div className={styles.savedHint}>
              {savedCount === 0
                ? 'Keine Antwort eingegeben.'
                : `${savedCount} Antwort(en) gespeichert.`}
            </div>
          )}
        </div>
      )}
    </>
  );
}

interface EditorData {
  matches: Match[];
  drafts: Record<number, PredictionDraft>;
  players: Player[];
}

function PredictionEditor({ userId, matchday }: { userId: number; matchday: AdminMatchday }) {
  const locked = isPastDeadline(matchday.deadline_at);

  // §13.0: KEINE Vorbefüllung fremder Kategorie-Tipps. Ergebnistipps werden
  // wie bisher über listPredictions vorbefüllt; das Backend liefert vor
  // Deadline laut Reconciliation-Regel ohnehin keine echten Fremd-Werte.
  const { data, loading, error } = useAsync<EditorData>(
    async (signal) => {
      const [matches, predictions, teams] = await Promise.all([
        adminApi.matchdayMatches(matchday.id, signal),
        adminApi.listPredictions(userId, matchday.id, signal).catch(() => []),
        adminApi.listTeams(signal).catch(() => [] as Team[]),
      ]);
      const target =
        teams.find((t) => t.name.toLowerCase().includes(TARGET_TEAM_HINT)) ?? teams[0] ?? null;
      const players = target
        ? await adminApi.listPlayers(target.id, signal).catch(() => [])
        : [];
      // §13.0: Vor Deadline maskiert das Backend home_pred/away_pred zu null
      // (nur has_tipped ist gesetzt) — dann NICHT vorbefüllen (Blind-Schreibpfad).
      const drafts: Record<number, PredictionDraft> = {};
      for (const m of matches) {
        const existing = predictions.find((p) => p.match_id === m.id);
        const hasValues = existing?.home_pred != null && existing.away_pred != null;
        drafts[m.id] = {
          home: hasValues ? String(existing!.home_pred) : '',
          away: hasValues ? String(existing!.away_pred) : '',
        };
      }
      return { matches, drafts, players };
    },
    [userId, matchday.id],
  );

  const [drafts, setDrafts] = useState<Record<number, PredictionDraft>>({});
  const [playerDrafts, setPlayerDrafts] = useState<Record<number, PlayerDraft>>({});
  const [attendanceInterval, setAttendanceInterval] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedCount, setSavedCount] = useState<number | null>(null);

  useEffect(() => {
    if (data) {
      setDrafts(data.drafts);
      setPlayerDrafts({});
      setAttendanceInterval('');
    }
  }, [data]);

  const matches = useMemo(() => data?.matches ?? [], [data]);
  const players = useMemo(() => data?.players ?? [], [data]);

  function handleChange(matchId: number, side: 'home' | 'away', value: string) {
    setSavedCount(null);
    setDrafts((prev) => {
      const current = prev[matchId] ?? { home: '', away: '' };
      return { ...prev, [matchId]: { ...current, [side]: value } };
    });
  }

  function handlePlayer(matchId: number, field: 'scorer' | 'assist', value: string) {
    setSavedCount(null);
    setPlayerDrafts((prev) => {
      const current = prev[matchId] ?? { scorer: '', assist: '' };
      return { ...prev, [matchId]: { ...current, [field]: value } };
    });
  }

  async function save() {
    setSaving(true);
    setSaveError(null);
    setSavedCount(null);
    try {
      let saved = 0;
      for (const m of matches) {
        const d = drafts[m.id];
        if (d && d.home !== '' && d.away !== '') {
          await adminApi.putPrediction({
            user_id: userId,
            match_id: m.id,
            home_pred: Number(d.home),
            away_pred: Number(d.away),
          });
          saved += 1;
        }
        const pd = playerDrafts[m.id];
        if (pd?.scorer && pd.scorer !== '') {
          await adminApi.putScorer(m.id, {
            user_id: userId,
            player_id: playerValueToId(pd.scorer),
          });
          saved += 1;
        }
        if (pd?.assist && pd.assist !== '') {
          await adminApi.putAssist(m.id, {
            user_id: userId,
            player_id: playerValueToId(pd.assist),
          });
          saved += 1;
        }
      }
      if (attendanceInterval !== '') {
        await adminApi.putAttendance(matchday.id, {
          user_id: userId,
          interval_index: Number(attendanceInterval),
        });
        saved += 1;
      }
      setSavedCount(saved);
    } catch (err) {
      if (err instanceof ApiError && err.status === 423) {
        setSaveError('Tippschluss überschritten — Tipps sind gesperrt.');
      } else {
        setSaveError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
      }
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Spinner label="Tipps werden geladen…" />;
  if (error) return <Alert>{error}</Alert>;

  return (
    <>
      <p className="muted">Tippschluss: {formatDateTime(matchday.deadline_at)}</p>

      {locked && (
        <Notice>Der Tippschluss ist überschritten. Tipps können nicht mehr erfasst werden.</Notice>
      )}

      {!locked && (
        <Notice>
          Hinweis: Bestehende Tipps der neuen Kategorien (Tor/Assist/Zuschauer) werden vor
          Tippschluss nicht angezeigt (Sichtbarkeitsregel). Eingaben überschreiben blind.
        </Notice>
      )}

      {matches.length === 0 && <Alert>Für diesen Spieltag sind keine Spiele hinterlegt.</Alert>}

      {matches.map((m) => (
        <div key={m.id}>
          <MatchRow
            match={m}
            draft={drafts[m.id] ?? { home: '', away: '' }}
            points={null}
            locked={locked}
            isPokal={matchday.kind === 'pokal'}
            onChange={handleChange}
          />
          {players.length > 0 && (
            <Card>
              <div className={styles.inlineForm}>
                <label className={styles.field} style={{ marginBottom: 0, flex: '1 1 140px' }}>
                  <span className={styles.label}>Tor</span>
                  <select
                    className={styles.categorySelect}
                    disabled={locked}
                    value={playerDrafts[m.id]?.scorer ?? ''}
                    onChange={(e) => handlePlayer(m.id, 'scorer', e.target.value)}
                  >
                    <option value="">— wählen —</option>
                    <option value={NONE_VALUE}>keiner</option>
                    {players.map((p) => (
                      <option key={p.id} value={String(p.id)}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className={styles.field} style={{ marginBottom: 0, flex: '1 1 140px' }}>
                  <span className={styles.label}>Assist</span>
                  <select
                    className={styles.categorySelect}
                    disabled={locked}
                    value={playerDrafts[m.id]?.assist ?? ''}
                    onChange={(e) => handlePlayer(m.id, 'assist', e.target.value)}
                  >
                    <option value="">— wählen —</option>
                    <option value={NONE_VALUE}>keiner</option>
                    {players.map((p) => (
                      <option key={p.id} value={String(p.id)}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </Card>
          )}
        </div>
      ))}

      {matches.length > 0 && (
        <Card>
          <label className={styles.field} style={{ marginBottom: 0, flex: '1 1 200px' }}>
            <span className={styles.label}>Zuschauer</span>
            <select
              className={styles.categorySelect}
              disabled={locked}
              value={attendanceInterval}
              onChange={(e) => {
                setSavedCount(null);
                setAttendanceInterval(e.target.value);
              }}
            >
              <option value="">— wählen —</option>
              {attendanceIntervals().map((iv) => (
                <option key={iv.index} value={String(iv.index)}>
                  {iv.label}
                </option>
              ))}
            </select>
          </label>
        </Card>
      )}

      {!locked && matches.length > 0 && (
        <div className={styles.saveBar}>
          {saveError && <Alert>{saveError}</Alert>}
          <Button onClick={() => void save()} disabled={saving}>
            {saving ? 'Speichern…' : 'Tipps speichern'}
          </Button>
          {savedCount !== null && (
            <div className={styles.savedHint}>
              {savedCount === 0 ? 'Keine Tipps eingegeben.' : `${savedCount} Tipp(s) gespeichert.`}
            </div>
          )}
        </div>
      )}
    </>
  );
}
